import { useState } from 'react';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Equipment } from '@models/equipment.model';
import { graphQlClient } from '@config/graphqlClient';
import EquipmentGraphQL from '@graphql/equipment.queries';

interface CreateEquipmentParams {
  name: string;
  supplierOwner: string;
  time: string;
  hoursUsed: number;
  amount: number;
  costPerHour: number;
  pricePerHour?: number;
  taskId: string;
  chargeToClient: boolean;
}

interface CreateEquipmentResponse {
  createEquipment: Omit<Equipment, 'equipmentId'>;
}

interface UseCreateEquipmentReturn {
  createEquipment: (params: CreateEquipmentParams) => Promise<Equipment | null>;
  loading: boolean;
}

export const useCreateEquipment = (): UseCreateEquipmentReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');

  const createEquipment = async (
    params: CreateEquipmentParams,
  ): Promise<Equipment | null> => {
    setLoading(true);

    try {
      const equipmentData = {
        name: params.name,
        supplierOwner: params.supplierOwner,
        time: params.time,
        hoursUsed: params.hoursUsed,
        amount: params.amount,
        costPerHour: params.costPerHour,
        pricePerHour: params.pricePerHour || params.costPerHour,
        taskId: params.taskId,
        chargeToClient: params.chargeToClient,
      };

      const data: CreateEquipmentResponse = await graphQlClient.request(
        EquipmentGraphQL.mutations.createEquipment,
        {
          equipment: equipmentData,
        },
      );

      if (data?.createEquipment) {
        dispatch(
          appendActionMessage({
            message: localeCommon['createdSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );

        // Generate a temporary ID for local state management
        // The actual ID will be assigned by the backend
        const equipmentWithTempId: Equipment = {
          ...data.createEquipment,
          equipmentId: `temp-${Date.now()}`,
        };

        return equipmentWithTempId;
      }

      return null;
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message:
            error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { createEquipment, loading };
};
